import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Trophy, Lock, Users, Clock, ChevronRight, Sparkles, Wallet, ShieldCheck, Loader2,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { getTournamentsPreview } from "@/lib/api";
import { shortAddress } from "@/lib/wallet";
import { evaluateTournament, ELIGIBILITY_STATE, groupByCategory, summarizeCategory } from "@/lib/eligibility";
import { useArcWallet, useArcBalance, useArcStatus, useArcConnect } from "@/context/ArcEcosystemContext";
import ArcDiscountBadge from "@/components/ArcDiscountBadge";

// Sprint 19 — single, in-flight connect handler. Used by every connect
// surface so duplicate clicks while a MetaMask popup is open are absorbed
// into the same promise and never trigger a second prompt.
function safeConnect(connect, connecting) {
  return (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (connecting) return;
    connect().catch(() => { /* error already surfaced via connectError + toast */ });
  };
}

function fmt(n) {
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function fmtCountdown(hours) {
  if (hours == null) return "—";
  if (hours < 1) return "starting soon";
  if (hours < 24) return `in ${hours}h`;
  const d = Math.floor(hours / 24);
  const h = hours % 24;
  return h ? `in ${d}d ${h}h` : `in ${d}d`;
}

export default function TournamentRail() {
  const [data, setData] = useState({ categories: [], tournaments: [] });
  const [activeCat, setActiveCat] = useState("open");

  const { wallet } = useArcWallet();
  const { connect, connecting, connectError } = useArcConnect();
  const { balance } = useArcBalance();
  const status = useArcStatus();

  // Static tournament config (loaded once)
  useEffect(() => {
    getTournamentsPreview().then(setData).catch(() => {});
  }, []);

  const ctx = useMemo(() => ({
    connected: status.connected,
    preLaunch: status.isPreLaunch,
    wrongNetwork: status.wrongNetwork,
    balance,
  }), [status.connected, status.isPreLaunch, status.wrongNetwork, balance]);

  const grouped = useMemo(() => groupByCategory(data.tournaments), [data.tournaments]);

  const totalUnlocked = useMemo(() => {
    let n = 0;
    for (const t of data.tournaments) if (evaluateTournament(t, ctx).eligible) n++;
    return n;
  }, [data.tournaments, ctx]);

  return (
    <section id="arena-access" className="relative py-24 sm:py-32" data-testid="tournament-rail-section">
      <div className="absolute inset-0 bg-grid-dense opacity-20 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* HEADER */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div className="max-w-2xl">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A855F7]">// arena access passport</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black mt-2">
              Where can your ARC <span className="shine-text">take you today</span>?
            </h2>
            <p className="mt-4 text-white/65 font-mono text-sm sm:text-base leading-relaxed">
              Live tournament eligibility for the connected wallet — projected directly from your
              on-chain ARC balance. No staking, no token locks, no approvals. Just access.
            </p>
          </div>
          <AccessSummary
            wallet={wallet}
            ctx={ctx}
            totalUnlocked={totalUnlocked}
            totalCount={data.tournaments.length}
            onConnect={connect}
            connecting={connecting}
            connectError={connectError}
          />
        </div>

        {/* Persistent V1.1 ARC discount badge — always visible across all states */}
        <div className="mb-6 -mt-2" data-testid="rail-arc-badge-strip">
          <ArcDiscountBadge variant="inline" testId="arc-badge-rail-header" />
        </div>

        {/* CATEGORY TABS */}
        <Tabs value={activeCat} onValueChange={setActiveCat} data-testid="tournament-tabs">
          <div className="overflow-x-auto -mx-4 px-4 mb-6">
            <TabsList className="bg-transparent p-0 h-auto gap-2 flex-wrap">
              {data.categories.map((c) => {
                const list = grouped[c.id] || [];
                const sum = summarizeCategory(list, ctx);
                return (
                  <TabsTrigger
                    key={c.id}
                    value={c.id}
                    className="bg-white/5 border border-white/10 text-white/70 px-4 py-2.5 font-mono text-xs uppercase tracking-[0.2em] data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:shadow-none"
                    style={
                      activeCat === c.id
                        ? { borderColor: c.color, color: c.color, boxShadow: `0 0 18px ${c.color}33` }
                        : {}
                    }
                    data-testid={`tournament-tab-${c.id}`}
                  >
                    <span className="w-2 h-2 mr-2" style={{ background: c.color, boxShadow: `0 0 8px ${c.color}` }} />
                    {c.label}
                    <span className="ml-2 text-white/40 normal-case tracking-normal">
                      {sum.unlocked}/{sum.total}
                    </span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {data.categories.map((c) => (
            <TabsContent key={c.id} value={c.id} className="m-0">
              <CategoryHeader category={c} />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5 mt-6">
                {(grouped[c.id] || []).map((t, i) => (
                  <TournamentCard
                    key={t.id}
                    tournament={t}
                    category={c}
                    ctx={ctx}
                    index={i}
                    onConnect={connect}
                    connecting={connecting}
                    connectError={connectError}
                  />
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* SECURITY STRIP */}
        <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#39FF14]" /> Read-only access projection</span>
            <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#00F0FF]" /> No token locks</span>
            <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#A855F7]" /> No approvals required</span>
          </div>
          <span className="text-white/30">eligibility · v1 off-chain</span>
        </div>
      </div>
    </section>
  );
}

/* ---------------- subcomponents ---------------- */

function AccessSummary({ wallet, ctx, totalUnlocked, totalCount, onConnect, connecting, connectError }) {
  if (!wallet?.address) {
    return (
      <div className="flex flex-col gap-2" data-testid="rail-connect-wrapper">
        <button
          onClick={safeConnect(onConnect, connecting)}
          disabled={connecting}
          aria-busy={connecting}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="rail-connect-button"
        >
          {connecting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span data-testid="rail-connect-button-label">Connecting…</span>
            </>
          ) : (
            <>
              <Wallet className="w-4 h-4" />
              <span data-testid="rail-connect-button-label">Connect to check access</span>
            </>
          )}
        </button>
        {connectError && !connecting && (
          <div
            className="font-mono text-[11px] text-[#FF3B30]"
            data-testid="rail-connect-error"
            role="alert"
          >
            {connectError}
          </div>
        )}
      </div>
    );
  }
  const pct = totalCount ? (totalUnlocked / totalCount) * 100 : 0;
  return (
    <div className="glass p-4 min-w-[260px]" data-testid="rail-access-summary">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Tournaments unlocked</span>
        <span className="font-mono text-[10px] text-white/40">{shortAddress(wallet.address)}</span>
      </div>
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <span className="font-display text-3xl font-black text-[#39FF14] glow-green" data-testid="rail-unlocked-count">
            {ctx.preLaunch ? "—" : totalUnlocked}
          </span>
          <span className="font-display text-xl font-bold text-white/40 ml-1">/ {totalCount}</span>
        </div>
        <div className="font-mono text-xs text-white/40">{ctx.preLaunch ? "pre-launch" : `${pct.toFixed(0)}%`}</div>
      </div>
      <Progress
        value={ctx.preLaunch ? 0 : pct}
        className="h-1.5 bg-white/5 mt-2 [&>div]:bg-gradient-to-r [&>div]:from-[#39FF14] [&>div]:to-[#00F0FF]"
      />
    </div>
  );
}

function CategoryHeader({ category }) {
  return (
    <div className="flex items-start gap-4 p-4 border" style={{ borderColor: `${category.color}33` }} data-testid={`category-header-${category.id}`}>
      <div className="w-10 h-10 grid place-items-center clip-hex flex-shrink-0" style={{ background: `${category.color}1A`, border: `1px solid ${category.color}55` }}>
        <Trophy className="w-5 h-5" style={{ color: category.color }} />
      </div>
      <div>
        <div className="flex items-center gap-3 mb-1">
          <h3 className="font-display text-lg font-bold tracking-wider" style={{ color: category.color }}>{category.label.toUpperCase()}</h3>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 border border-white/10 text-white/60">
            min {fmt(category.required_min_arc)} ARC
          </span>
        </div>
        <p className="font-mono text-xs text-white/55 max-w-2xl">{category.description}</p>
      </div>
    </div>
  );
}

function TournamentCard({ tournament: t, category: c, ctx, index, onConnect, connecting, connectError }) {
  const result = evaluateTournament(t, ctx);
  const playersPct = (t.current_players / t.max_players) * 100;
  const filled = result.eligible;
  const cardColor = filled ? c.color : "#374151";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="glass p-5 relative overflow-hidden flex flex-col gap-4"
      style={{ borderColor: filled ? `${c.color}66` : "rgba(255,255,255,0.08)" }}
      data-testid={`tournament-card-${t.id}`}
    >
      {/* Status ribbon */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2" style={{ background: cardColor, boxShadow: `0 0 8px ${cardColor}` }} />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: cardColor }}>
            {c.label}
          </span>
        </div>
        <StatusBadge state={result.state} color={c.color} />
      </div>

      {/* Title + meta */}
      <div>
        <h4 className="font-display text-lg font-bold leading-tight" data-testid={`tournament-title-${t.id}`}>{t.title}</h4>
        <div className="font-mono text-[11px] text-white/50 mt-1">
          {t.game} · {t.format}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 border-t border-white/5 pt-3">
        <Stat label="Pool" value={`${fmt(t.prize_pool_arc)} ARC`} />
        <Stat label="Entry" value={`${fmt(t.entry_fee_arc)} ARC`} />
        <Stat label="Starts" value={fmtCountdown(t.starts_in_hours)} icon={Clock} />
      </div>

      {/* Players bar */}
      <div>
        <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1">
          <span className="flex items-center gap-1.5"><Users className="w-3 h-3" /> {t.current_players}/{t.max_players}</span>
          <span>{playersPct.toFixed(0)}% filled</span>
        </div>
        <div className="h-1 bg-white/5 overflow-hidden">
          <div className="h-full transition-all" style={{ width: `${Math.min(100, playersPct)}%`, background: cardColor }} />
        </div>
      </div>

      {/* Eligibility footer */}
      <EligibilityFooter
        result={result}
        t={t}
        c={c}
        ctx={ctx}
        onConnect={onConnect}
        connecting={connecting}
        connectError={connectError}
      />
    </motion.div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">{label}</div>
      <div className="font-display text-sm font-bold text-white truncate flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3 text-white/50" />} {value}
      </div>
    </div>
  );
}

function StatusBadge({ state, color }) {
  if (state === ELIGIBILITY_STATE.UNLOCKED) {
    return (
      <span
        className="font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 inline-flex items-center gap-1"
        style={{ color, border: `1px solid ${color}55`, background: `${color}10` }}
        data-testid="status-unlocked"
      >
        <Sparkles className="w-3 h-3" /> Ready to enter
      </span>
    );
  }
  if (state === ELIGIBILITY_STATE.WRONG_NETWORK) {
    return (
      <span
        className="font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 inline-flex items-center gap-1 text-[#FF3B30] border border-[#FF3B30]/40"
        data-testid="status-wrong-network"
      >
        <Lock className="w-3 h-3" /> Switch network
      </span>
    );
  }
  if (state === ELIGIBILITY_STATE.PRE_LAUNCH) {
    return (
      <span
        className="font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 inline-flex items-center gap-1 text-[#F59E0B] border border-[#F59E0B]/40"
        data-testid="status-prelaunch"
      >
        <Clock className="w-3 h-3" /> Pre-launch
      </span>
    );
  }
  if (state === ELIGIBILITY_STATE.CONNECT_REQUIRED) {
    return (
      <span
        className="font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 inline-flex items-center gap-1 text-white/50 border border-white/15"
        data-testid="status-connect"
      >
        <Wallet className="w-3 h-3" /> Connect
      </span>
    );
  }
  return (
    <span
      className="font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 inline-flex items-center gap-1 text-white/55 border border-white/15"
      data-testid="status-locked"
    >
      <Lock className="w-3 h-3" /> Locked
    </span>
  );
}

function EligibilityFooter({ result, t, c, ctx, onConnect, connecting, connectError }) {
  const { state, eligible, shortfall } = result;
  const target = t.required_min_arc;
  const progressPct = target > 0 ? Math.min(100, (ctx.balance / target) * 100) : 100;

  if (state === ELIGIBILITY_STATE.UNLOCKED) {
    return (
      <div className="flex items-center justify-between border-t border-white/5 pt-3 gap-2">
        <span className="font-mono text-[10px] text-[#39FF14] uppercase tracking-[0.2em] flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" /> Tier <strong className="font-bold ml-0.5">{c.label}</strong>
        </span>
        <div className="flex items-center gap-2">
          <ArcDiscountBadge variant="inline" testId={`arc-badge-${t.id}`} />
          <button
            className="btn-primary px-3 py-1.5 text-[10px] inline-flex items-center gap-1"
            onClick={(e) => { e.preventDefault(); }}
            data-testid={`enter-button-${t.id}`}
          >
            Enter <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  if (state === ELIGIBILITY_STATE.CONNECT_REQUIRED) {
    return (
      <div className="border-t border-white/5 pt-3 space-y-2">
        <button
          onClick={safeConnect(onConnect, connecting)}
          disabled={connecting}
          aria-busy={connecting}
          className="btn-secondary w-full text-[11px] py-2 inline-flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid={`unlock-button-${t.id}`}
        >
          {connecting ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span data-testid={`unlock-button-label-${t.id}`}>Connecting…</span>
            </>
          ) : (
            <span data-testid={`unlock-button-label-${t.id}`}>Connect Wallet to Check</span>
          )}
        </button>
        {connectError && !connecting && (
          <div
            className="font-mono text-[10px] text-[#FF3B30]"
            data-testid={`unlock-error-${t.id}`}
            role="alert"
          >
            {connectError}
          </div>
        )}
      </div>
    );
  }

  if (state === ELIGIBILITY_STATE.WRONG_NETWORK) {
    return (
      <div className="border-t border-white/5 pt-3" data-testid={`wrong-network-footer-${t.id}`}>
        <div className="font-mono text-[11px] text-[#FF3B30]">Switch your wallet to BNB Smart Chain to read eligibility.</div>
      </div>
    );
  }

  const targetLabel = ctx.preLaunch
    ? (target === 0 ? "Open to all post-launch" : `Unlocks at TGE · ${fmt(target)} ARC threshold`)
    : `${fmt(shortfall)} more ARC to unlock ${c.label}`;

  return (
    <div className="border-t border-white/5 pt-3 space-y-2" data-testid={`locked-footer-${t.id}`}>
      <div className="font-mono text-[11px] text-white/65">{targetLabel}</div>
      {!ctx.preLaunch && target > 0 && (
        <>
          <div className="h-1 bg-white/5 overflow-hidden">
            <div
              className="h-full transition-all"
              style={{ width: `${progressPct}%`, background: c.color, opacity: 0.6 }}
            />
          </div>
          <div className="flex justify-between font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">
            <span>{progressPct.toFixed(0)}% of threshold</span>
            <span>{fmt(target)} ARC required</span>
          </div>
        </>
      )}
    </div>
  );
}
